import type { GetDocumentByPathQuery } from '#src/graphql/generated';
import type { MilkdownEditorHandle } from './editor';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  Popover,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FiEdit3, FiMoreVertical } from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { EditDocumentProvider, useEditDocument } from './edit-document-context';
import { EditDocumentEditor } from './editor';
import { PromptFooterInput } from './prompt-command';
import { uploadImageFile, uploadPastedFile } from './upload-image';

import { LightMode } from '#src/components/ui/color-mode';
import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { useAppConfig } from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import {
  usePageFooter,
  usePageHeader,
} from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import {
  isValidDocumentPath,
  DOCUMENT_PATH_DESCRIPTION,
} from '#src/utils/document-path';
import { formatDocumentName } from '#src/utils/format-document-name';

type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

interface ViewDocumentProps {
  /** Document from parent query (Document or DocumentNotFound). */
  document: DocumentByPathResult;
  /** Canonical path (document path when found, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

function ViewDocumentFooterContent({
  documentId,
  isNewDocument,
}: {
  documentId: string | undefined;
  isNewDocument: boolean;
}) {
  const { setValue } = useEditDocument();
  return (
    <PromptFooterInput
      documentId={documentId}
      isNewDocument={isNewDocument}
      onValueUpdate={setValue}
    />
  );
}

interface DocumentChildrenButtonProps {
  parentPath: string;
  items: Array<{ id: string; path: string }>;
  hasMore: boolean;
}

function DocumentChildrenButton({
  parentPath,
  items,
  hasMore,
}: DocumentChildrenButtonProps) {
  const countLabel = hasMore ? `${items.length}+` : `${items.length}`;

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }} size="sm">
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          color="fg.muted"
          fontWeight="normal"
          fontSize="sm"
          px={2}
          minW={0}
          title="Child documents"
        >
          🔗 {countLabel}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="320px"
            maxH="300px"
            overflowY="auto"
            p={3}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={1} align="stretch">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
                Child documents
              </Text>
              {items.map((child) => (
                <Link
                  key={child.id}
                  asChild
                  display="block"
                  fontSize="sm"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  <RouterLink to={`/documents/${child.path}`}>
                    {formatDocumentName(
                      getRelativePath(parentPath, child.path),
                    ).join(' › ')}
                  </RouterLink>
                </Link>
              ))}
              {hasMore && (
                <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
                  More than {items.length} children (showing first{' '}
                  {items.length})
                </Text>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

function DocumentBreadcrumb({ path }: { path: string }) {
  const segments = useMemo(() => {
    const rawSegments = path.split('/').filter((part) => part.length > 0);
    const formatted = formatDocumentName(path);
    const result: Array<{ label: string; accumulated: string }> = [];
    let accumulated = '';

    rawSegments.forEach((segment, index) => {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      result.push({
        label: formatted[index] ?? segment,
        accumulated,
      });
    });

    return result;
  }, [path]);

  if (segments.length === 0) {
    const fallback = formatDocumentName(path).join(' > ') || path;
    return (
      <Text fontSize="lg" fontWeight="semibold" color="fg">
        {fallback}
      </Text>
    );
  }

  return (
    <HStack gap={1} align="center" flexWrap="wrap">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        return (
          <Fragment key={segment.accumulated}>
            {isLast ? (
              <Text fontSize="lg" fontWeight="semibold" color="fg">
                {segment.label}
              </Text>
            ) : (
              <Link
                asChild
                fontSize="lg"
                fontWeight="semibold"
                color="blue.600"
                _hover={{ textDecoration: 'underline' }}
              >
                <RouterLink to={`/documents/${segment.accumulated}`}>
                  {segment.label}
                </RouterLink>
              </Link>
            )}
            {!isLast && (
              <Text fontSize="lg" fontWeight="semibold" color="fg.subtle">
                ›
              </Text>
            )}
          </Fragment>
        );
      })}
    </HStack>
  );
}

interface RenameDocumentButtonProps {
  currentPath: string;
  getCurrentValue: () => string;
  onRename: (newPath: string, currentValue: string) => Promise<void>;
}

function RenameDocumentButton({
  currentPath,
  getCurrentValue,
  onRename,
}: RenameDocumentButtonProps) {
  const { documentPathRegex } = useAppSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [renamePath, setRenamePath] = useState(currentPath);

  useEffect(() => {
    if (isOpen) setRenamePath(currentPath);
  }, [isOpen, currentPath]);

  const trimmedRenamePath = renamePath.trim();
  const isRenamePathValid = trimmedRenamePath
    ? isValidDocumentPath(trimmedRenamePath, documentPathRegex)
    : false;

  const handleRename = async () => {
    if (!trimmedRenamePath || !isRenamePathValid) return;
    try {
      await onRename(trimmedRenamePath, getCurrentValue());
      setIsOpen(false);
    } catch {
      // Parent shows toaster; keep popover open
    }
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <IconButton
          aria-label="Rename document"
          variant="ghost"
          size="sm"
          title="Rename document"
        >
          <FiEdit3 />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                Rename document
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Enter the new path for this document.
              </Text>
              <Input
                placeholder="e.g. folder/doc-name or path (with parens)"
                value={renamePath}
                onChange={(e) => setRenamePath(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRename();
                  if (e.key === 'Escape') setIsOpen(false);
                }}
                size="sm"
                autoFocus
                aria-invalid={!!trimmedRenamePath && !isRenamePathValid}
                borderColor={
                  trimmedRenamePath && !isRenamePathValid
                    ? 'red.500'
                    : undefined
                }
              />
              {trimmedRenamePath && !isRenamePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
              <HStack gap={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => void handleRename()}
                  disabled={!trimmedRenamePath || !isRenamePathValid}
                >
                  Rename
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

/** Path from DocumentResult (Document or DocumentNotFound). */
function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

/** Children data from DocumentResult. */
function getChildrenFromResult(result: DocumentByPathResult) {
  const children = result?.children;
  return {
    items: children?.items ?? [],
    hasMore: children?.pagination?.hasMore ?? false,
  };
}

/** Part of child path after parent path (for display). */
function getRelativePath(parentPath: string, childPath: string): string {
  if (!parentPath) return childPath;
  if (childPath === parentPath) return '';
  const prefix = parentPath + '/';
  return childPath.startsWith(prefix)
    ? childPath.slice(prefix.length)
    : childPath;
}

function ViewDocumentContent({ document, path, refetch }: ViewDocumentProps) {
  const navigate = useNavigate();
  const { setTitle, actionsRef } = usePageHeader();
  const { footerRef, setFooterMode } = usePageFooter();
  const {
    config,
    recordRecentlyOpenedDocument,
    removeRecentlyOpenedDocumentById,
  } = useAppConfig();
  const { documentPathRegex } = useAppSettings();
  const user = useCurrentUser();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  // New doc pages: start with TOC; switch to editor when Create is clicked
  const [isEditingNewDocument, setEditingNewDocument] = useState(false);
  // Add popover (TOC): next path segment to navigate to
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addSegment, setAddSegment] = useState('');
  // Upload popover (header): raw string to set document content
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadRawValue, setUploadRawValue] = useState('');
  useEffect(() => {
    setEditingNewDocument(false);
  }, [displayPath]);

  const showEditor = !isNewDocument || isEditingNewDocument;

  // Primitive deps only to avoid infinite loop (document/children refs can churn)
  const childrenData = useMemo(
    () => getChildrenFromResult(document),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- document ref churn causes loop
    [
      document?.__typename,
      document?.children?.items?.length,
      document?.children?.pagination?.hasMore,
    ],
  );

  useEffect(() => {
    setFooterMode('fixed');
    return () => setFooterMode('float');
  }, [setFooterMode]);

  useEffect(() => {
    if (documentId) {
      recordRecentlyOpenedDocument(documentId, path);
    }
  }, [documentId, path, recordRecentlyOpenedDocument]);

  const editorRef = useRef<MilkdownEditorHandle>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedValue, setLastSavedValue] = useState(
    document?.__typename === 'Document' ? (document.value ?? '') : '',
  );

  // Sync lastSavedValue when document loads or changes externally (e.g. initial load)
  useEffect(() => {
    const value =
      document?.__typename === 'Document' ? (document.value ?? '') : '';
    setLastSavedValue(value);
  }, [document]);

  const formattedName = formatDocumentName(displayPath).join(' > ');

  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);

  const handleSave = useCallback(async () => {
    const value = editorRef.current?.getMarkdown() ?? '';
    try {
      const { data } = await upsertDocument({
        variables: {
          input: {
            id: documentId ?? path,
            path: isNewDocument ? path : displayPath,
            value,
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: isNewDocument
          ? 'Document created successfully'
          : 'Document saved successfully',
      });

      // Use saved value from server response; sync editor BEFORE refetch to avoid
      // refetch-triggered re-renders resetting the editor with stale cached data
      const savedValue = data?.upsertDocument?.document?.value ?? value;
      editorRef.current?.resetContent(savedValue);
      setLastSavedValue(savedValue);
      setHasChanges(false);

      await refetch();

      // Navigate to the document path to refresh
      if (isNewDocument) {
        navigate(`/documents/${path}`, { replace: true });
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Error saving document: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      });
    }
  }, [
    documentId,
    path,
    displayPath,
    isNewDocument,
    upsertDocument,
    refetch,
    navigate,
  ]);

  // Handler for image uploads in the editor (drag-drop, paste)
  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadImageFile(file, {
        apiHost: config.apiHost,
        userEmail: user.email || '',
      });
      return result.url;
    },
    [config.apiHost, user.email],
  );

  // Handler for pasted files (images → embed, others → link)
  const handleFilePaste = useCallback(
    async (
      files: File[],
      insert: (markdown: string) => void,
    ): Promise<void> => {
      const options = {
        apiHost: config.apiHost,
        userEmail: user.email || '',
      };
      for (const file of files) {
        try {
          const { markdown } = await uploadPastedFile(file, options);
          insert(markdown);
        } catch (error) {
          toaster.create({
            title: 'Upload failed',
            description:
              error instanceof Error
                ? error.message
                : `Failed to upload ${file.name}`,
          });
        }
      }
    },
    [config.apiHost, user.email],
  );

  const handleRename = useCallback(
    async (newPath: string, currentValue: string) => {
      if (document?.__typename !== 'Document') return;
      try {
        await upsertDocument({
          variables: {
            input: {
              id: document.id,
              path: newPath,
              value: currentValue,
            },
          },
        });
        toaster.create({
          title: 'Success',
          description: 'Document renamed.',
        });
        removeRecentlyOpenedDocumentById(document.id);
        await refetch();
        navigate(`/documents/${newPath}`, { replace: true });
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    },
    [
      document,
      upsertDocument,
      refetch,
      navigate,
      removeRecentlyOpenedDocumentById,
    ],
  );

  const hasChildren = childrenData.items.length > 0;
  const documentPathForRename =
    document?.__typename === 'Document' ? document.path : displayPath;

  useEffect(() => {
    const rightControls = (
      <HStack gap={2} align="center">
        {hasChildren && showEditor && (
          <DocumentChildrenButton
            parentPath={displayPath}
            items={childrenData.items}
            hasMore={childrenData.hasMore}
          />
        )}
        {showEditor && (
          <>
            <Popover.Root
              open={isUploadOpen}
              onOpenChange={(e) => {
                setIsUploadOpen(e.open);
                if (!e.open) setUploadRawValue('');
              }}
              positioning={{ placement: 'bottom-end' }}
              size="sm"
            >
              <Popover.Trigger asChild>
                <Box as="span" display="none" />
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content
                    maxW="480px"
                    w="90vw"
                    p={4}
                    bg="bg"
                    border="1px solid"
                    borderColor="border"
                    shadow="lg"
                    borderRadius="md"
                  >
                    <Popover.Arrow>
                      <Popover.ArrowTip />
                    </Popover.Arrow>
                    <VStack gap={3} align="stretch">
                      <Text fontSize="sm" fontWeight="semibold" color="fg">
                        Set document content
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        Paste or type raw text to replace the entire document
                        content. You can save afterward.
                      </Text>
                      <Textarea
                        placeholder="Paste or type content..."
                        value={uploadRawValue}
                        onChange={(e) => setUploadRawValue(e.target.value)}
                        size="sm"
                        minH="120px"
                        fontFamily="mono"
                        fontSize="sm"
                        resize="vertical"
                      />
                      <HStack gap={2} justify="flex-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsUploadOpen(false);
                            setUploadRawValue('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={() => {
                            editorRef.current?.resetContent(uploadRawValue);
                            setLastSavedValue(uploadRawValue);
                            setIsUploadOpen(false);
                            setUploadRawValue('');
                          }}
                        >
                          Apply
                        </Button>
                      </HStack>
                    </VStack>
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
            <Menu.Root positioning={{ placement: 'bottom-end' }}>
              <Menu.Trigger asChild>
                <IconButton
                  aria-label="Document actions"
                  variant="ghost"
                  size="sm"
                >
                  <FiMoreVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      value="upload"
                      onClick={() => setIsUploadOpen(true)}
                    >
                      Upload
                    </Menu.Item>
                    {!isNewDocument && (
                      <Menu.Item
                        value="history"
                        onClick={() =>
                          navigate(`/documents/${displayPath}/history`)
                        }
                      >
                        History
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </>
        )}
        {isNewDocument && !isEditingNewDocument ? (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => setEditingNewDocument(true)}
          >
            Create
          </Button>
        ) : isNewDocument && isEditingNewDocument ? (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleSave}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </Button>
        ) : (
          !isNewDocument && (
            <RenameDocumentButton
              currentPath={documentPathForRename}
              getCurrentValue={() => editorRef.current?.getMarkdown() ?? ''}
              onRename={handleRename}
            />
          )
        )}
      </HStack>
    );

    const titleContent = (
      <HStack
        gap={4}
        align="center"
        justify="space-between"
        width="100%"
        minW={0}
      >
        <Box flex="1" minW={0}>
          <DocumentBreadcrumb path={displayPath} />
        </Box>
        {rightControls}
      </HStack>
    );
    setTitle(titleContent);
    return () => setTitle(null);
  }, [
    formattedName,
    setTitle,
    isNewDocument,
    isEditingNewDocument,
    documentId,
    documentPathForRename,
    handleRename,
    handleSave,
    saving,
    hasChildren,
    childrenData,
    displayPath,
    showEditor,
    isUploadOpen,
    uploadRawValue,
    navigate,
  ]);

  // Track changes in the editor
  useEffect(() => {
    const checkForChanges = () => {
      const currentValue = editorRef.current?.getMarkdown() ?? '';
      setHasChanges(currentValue !== lastSavedValue);
    };

    // Check periodically for changes
    const interval = setInterval(checkForChanges, 500);
    return () => clearInterval(interval);
  }, [lastSavedValue]);

  const actionsContent =
    showEditor && hasChanges ? (
      <Button
        size="sm"
        colorScheme="blue"
        onClick={handleSave}
        loading={saving}
        loadingText="Saving..."
      >
        Save
      </Button>
    ) : null;

  const footerContent = showEditor ? (
    <ViewDocumentFooterContent
      documentId={documentId}
      isNewDocument={isNewDocument}
    />
  ) : null;

  if (isNewDocument && !isEditingNewDocument) {
    return (
      <>
        <Box bg="bg" shadow="lg" borderRadius="lg" height="100%" px={6} py={6}>
          <VStack gap={4} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              This document does not exist yet. Create it to add content, or
              open a child document below.
            </Text>
            {childrenData.items.length > 0 ? (
              <VStack gap={2} align="stretch">
                <Text fontSize="sm" fontWeight="semibold" color="fg">
                  Child documents
                </Text>
                <VStack gap={1} align="stretch" asChild>
                  <Box as="ul" listStyleType="none" pl={0} m={0}>
                    {childrenData.items.map((child) => (
                      <Box as="li" key={child.id}>
                        <Link
                          asChild
                          display="block"
                          fontSize="sm"
                          color="blue.600"
                          _hover={{ textDecoration: 'underline' }}
                          py={1}
                        >
                          <RouterLink to={`/documents/${child.path}`}>
                            {formatDocumentName(
                              getRelativePath(displayPath, child.path),
                            ).join(' › ')}
                          </RouterLink>
                        </Link>
                      </Box>
                    ))}
                  </Box>
                </VStack>
                {childrenData.hasMore && (
                  <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
                    More than {childrenData.items.length} children
                  </Text>
                )}
              </VStack>
            ) : null}
            <Popover.Root
              open={isAddOpen}
              onOpenChange={(e) => {
                setIsAddOpen(e.open);
                if (!e.open) setAddSegment('');
              }}
              positioning={{ placement: 'top-start' }}
              size="sm"
            >
              <Popover.Trigger asChild>
                <Button variant="outline" size="sm" width="fit-content">
                  Add
                </Button>
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content
                    maxW="400px"
                    p={4}
                    bg="bg"
                    border="1px solid"
                    borderColor="border"
                    shadow="lg"
                    borderRadius="md"
                  >
                    <Popover.Arrow>
                      <Popover.ArrowTip />
                    </Popover.Arrow>
                    <VStack gap={3} align="stretch">
                      <Text fontSize="sm" fontWeight="semibold" color="fg">
                        Go to path
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        Enter the next part of the path (e.g. new-page or
                        folder/page).
                      </Text>
                      <Input
                        placeholder={
                          displayPath
                            ? `e.g. new-page or nested/page`
                            : `e.g. my-doc or folder/doc`
                        }
                        value={addSegment}
                        onChange={(e) => setAddSegment(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = addSegment.trim();
                            const fullPath = displayPath
                              ? `${displayPath}/${trimmed}`
                              : trimmed;
                            if (
                              trimmed &&
                              isValidDocumentPath(fullPath, documentPathRegex)
                            ) {
                              setIsAddOpen(false);
                              setAddSegment('');
                              navigate(`/documents/${fullPath}`);
                            }
                          }
                          if (e.key === 'Escape') setIsAddOpen(false);
                        }}
                        size="sm"
                        autoFocus
                        aria-invalid={
                          !!addSegment.trim() &&
                          !isValidDocumentPath(
                            displayPath
                              ? `${displayPath}/${addSegment.trim()}`
                              : addSegment.trim(),
                            documentPathRegex,
                          )
                        }
                        borderColor={
                          addSegment.trim() &&
                          !isValidDocumentPath(
                            displayPath
                              ? `${displayPath}/${addSegment.trim()}`
                              : addSegment.trim(),
                            documentPathRegex,
                          )
                            ? 'red.500'
                            : undefined
                        }
                      />
                      {addSegment.trim() &&
                        !isValidDocumentPath(
                          displayPath
                            ? `${displayPath}/${addSegment.trim()}`
                            : addSegment.trim(),
                          documentPathRegex,
                        ) && (
                          <Text fontSize="xs" color="red.500">
                            {DOCUMENT_PATH_DESCRIPTION}
                          </Text>
                        )}
                      <HStack gap={2} justify="flex-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsAddOpen(false);
                            setAddSegment('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={() => {
                            const trimmed = addSegment.trim();
                            const fullPath = displayPath
                              ? `${displayPath}/${trimmed}`
                              : trimmed;
                            if (
                              trimmed &&
                              isValidDocumentPath(fullPath, documentPathRegex)
                            ) {
                              setIsAddOpen(false);
                              setAddSegment('');
                              navigate(`/documents/${fullPath}`);
                            }
                          }}
                          disabled={
                            !addSegment.trim() ||
                            !isValidDocumentPath(
                              displayPath
                                ? `${displayPath}/${addSegment.trim()}`
                                : addSegment.trim(),
                              documentPathRegex,
                            )
                          }
                        >
                          Go
                        </Button>
                      </HStack>
                    </VStack>
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
          </VStack>
        </Box>
      </>
    );
  }

  return (
    <EditDocumentProvider editorRef={editorRef}>
      {actionsRef.current &&
        actionsContent &&
        createPortal(actionsContent, actionsRef.current)}
      {footerRef.current &&
        footerContent &&
        createPortal(footerContent, footerRef.current)}

      <Box
        bg="bg"
        shadow="lg"
        borderRadius="lg"
        height="100%"
        px={6}
        pb={6}
        pt={0}
      >
        <VStack gap={4} align="stretch" height="100%">
          <Box flex={1} overflowY="auto" minHeight={0}>
            <Box flex={1} minHeight={0} display="flex" flexDirection="column">
              <LightMode>
                <Box
                  flex={1}
                  minHeight={0}
                  display="flex"
                  flexDirection="column"
                >
                  <EditDocumentEditor
                    key={documentId ?? path}
                    ref={editorRef}
                    value={
                      document?.__typename === 'Document'
                        ? (document.value ?? '')
                        : ''
                    }
                    onUpload={handleImageUpload}
                    onFilePaste={handleFilePaste}
                  />
                </Box>
              </LightMode>
            </Box>
          </Box>
        </VStack>
      </Box>
    </EditDocumentProvider>
  );
}

export function ViewDocument({ document, path, refetch }: ViewDocumentProps) {
  return (
    <ViewDocumentContent document={document} path={path} refetch={refetch} />
  );
}
